import { useEffect, useId, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { SignOutButton, useUser } from '@/shared/auth';
import { Card } from '@/shared/components/ui/Card';
import { Toggle } from '@/shared/components/ui/Toggle';
import { FIELD_CONTROL_CLASS } from '@/shared/components/ui/form';
import { LocaleSwitcher } from '@/shared/i18n/LocaleSwitcher';
import { useI18n } from '@/shared/i18n/useT';
import { ThemeSwitch, useMotion } from '@/shared/theme';
import { getContrastTextColor, initialsOf } from '@/shared/components/ui/Avatar';
import { ResetAppDataCard } from '@/shared/pwa/ResetAppDataCard';
import { ColorPicker } from './ColorPicker';

/**
 * The settings that belong to the person, not to the organization: language,
 * theme, and the session itself.
 *
 * They are separated from the org sections on purpose. Changing your own
 * language affects nobody; changing the grid alignment changes it for every
 * colleague, and a screen that mixes the two invites the second by accident.
 */
export function PreferencesSection() {
  const { t } = useI18n();
  const user = useUser();
  const motion = useMotion();
  const nameId = useId();
  const [name, setName] = useState('');
  const [savedName, setSavedName] = useState('');
  const [nameStatus, setNameStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [avatarColor, setAvatarColor] = useState<string | null>(null);
  const [savedAvatarColor, setSavedAvatarColor] = useState<string | null>(null);
  const [avatarColorStatus, setAvatarColorStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  useEffect(() => {
    if (!user) {
      setName('');
      setSavedName('');
      setAvatarColor(null);
      setSavedAvatarColor(null);
      return;
    }

    const metadataName = typeof user.user_metadata.full_name === 'string'
      ? user.user_metadata.full_name.trim()
      : '';
    setName(metadataName);
    setSavedName(metadataName);

    const metadataColor = typeof user.user_metadata.avatar_color === 'string' && user.user_metadata.avatar_color.trim()
      ? user.user_metadata.avatar_color.trim()
      : null;
    setAvatarColor(metadataColor);
    setSavedAvatarColor(metadataColor);

    let active = true;
    void supabase
      .from('profiles')
      .select('full_name')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        const profileName = data?.full_name?.trim();
        if (!active || !profileName) return;
        setName(profileName);
        setSavedName(profileName);
      });

    return () => {
      active = false;
    };
  }, [user]);

  async function saveName() {
    const nextName = name.trim();
    if (!user || nextName === '' || nextName === savedName) return;

    setNameStatus('saving');
    const { error: authError } = await supabase.auth.updateUser({
      data: { full_name: nextName },
    });
    if (authError) {
      setNameStatus('error');
      return;
    }

    const { error: profileError } = await supabase
      .from('profiles')
      .update({ full_name: nextName })
      .eq('id', user.id);
    if (profileError) {
      setNameStatus('error');
      return;
    }

    setName(nextName);
    setSavedName(nextName);
    setNameStatus('saved');
  }

  async function saveAvatarColor(nextColor: string | null) {
    const normalized = nextColor && nextColor.trim() ? nextColor.trim() : null;
    if (!user || normalized === savedAvatarColor) {
      setAvatarColor(normalized);
      return;
    }

    setAvatarColor(normalized);
    setAvatarColorStatus('saving');

    const { error: authError } = await supabase.auth.updateUser({
      data: { avatar_color: normalized },
    });
    if (authError) {
      setAvatarColorStatus('error');
      return;
    }

    setSavedAvatarColor(normalized);
    setAvatarColorStatus('saved');
  }

  const displayName = name.trim() || (typeof user?.email === 'string' ? user.email : '');
  const initials = initialsOf(displayName);

  return (
    <div className="flex flex-col gap-4">
      <Card title={t('settings.profile.name.title')} description={t('settings.profile.name.description')}>
        <div className="flex max-w-sm flex-col gap-3">
          <label htmlFor={nameId} className="sr-only">
            {t('settings.profile.name.label')}
          </label>
          <input
            id={nameId}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setNameStatus('idle');
            }}
            disabled={!user || nameStatus === 'saving'}
            autoComplete="name"
            className={FIELD_CONTROL_CLASS}
          />
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => void saveName()}
              disabled={!user || name.trim() === '' || name.trim() === savedName || nameStatus === 'saving'}
              className="nt-control"
            >
              {nameStatus === 'saving' ? t('settings.profile.name.saving') : t('common.save')}
            </button>
            {nameStatus === 'saved' && (
              <p aria-live="polite" className="text-sm text-muted">
                {t('settings.profile.name.saved')}
              </p>
            )}
            {nameStatus === 'error' && (
              <p role="alert" className="text-sm text-danger">
                {t('settings.profile.name.saveError')}
              </p>
            )}
          </div>
        </div>
      </Card>

      <Card
        title={t('settings.profile.color.title')}
        description={t('settings.profile.color.description')}
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <ColorPicker
              valor={avatarColor}
              disabled={!user || avatarColorStatus === 'saving'}
              onChange={(next) => void saveAvatarColor(next)}
              trigger={
                <span
                  role="button"
                  tabIndex={0}
                  className="flex items-center gap-3 rounded-2xl border border-border bg-surface-2 px-3.5 py-2 text-left transition-colors hover:bg-surface-3 cursor-pointer"
                >
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold shadow-sm ${
                      avatarColor ? '' : 'bg-accent text-accent-fg'
                    }`}
                    style={
                      avatarColor
                        ? { backgroundColor: avatarColor, color: getContrastTextColor(avatarColor) }
                        : undefined
                    }
                  >
                    {initials}
                  </span>
                  <span className="flex flex-col">
                    <span className="text-xs font-semibold text-fg">
                      {avatarColor ? avatarColor.toUpperCase() : t('settings.profile.color.default')}
                    </span>
                    <span className="text-[0.6875rem] text-muted">
                      {t('settings.profile.color.pick')}
                    </span>
                  </span>
                  <span className="ml-1 text-xs text-muted" aria-hidden="true">▾</span>
                </span>
              }
            />
            {avatarColor && (
              <button
                type="button"
                onClick={() => void saveAvatarColor(null)}
                disabled={!user || avatarColorStatus === 'saving'}
                className="nt-control text-xs"
              >
                {t('settings.profile.color.reset')}
              </button>
            )}
          </div>
          {avatarColorStatus === 'saving' && (
            <p aria-live="polite" className="text-sm text-muted">
              {t('settings.profile.color.saving')}
            </p>
          )}
          {avatarColorStatus === 'saved' && (
            <p aria-live="polite" className="text-sm text-muted">
              {t('settings.profile.color.saved')}
            </p>
          )}
          {avatarColorStatus === 'error' && (
            <p role="alert" className="text-sm text-danger">
              {t('settings.profile.color.saveError')}
            </p>
          )}
        </div>
      </Card>

      <Card
        title={t('settings.language.title')}
        description={t('settings.language.description')}
      >
        <LocaleSwitcher />
      </Card>

      <Card
        title={t('settings.theme.title')}
        description={t('settings.theme.description')}
      >
        <div className="flex flex-wrap items-center gap-3">
          <ThemeSwitch className="w-auto min-w-[18rem]" />
        </div>
      </Card>

      <Card
        title={t('settings.motion.title')}
        description={t('settings.motion.description')}
      >
        <div className="flex flex-col gap-2">
          <Toggle
            checked={motion.enabled}
            onChange={(next) => motion.setPreference(next)}
            label={t('settings.motion.label')}
          />
          {motion.preference === null && (
            <p className="text-xs text-muted">{t('settings.motion.followingSystem')}</p>
          )}
        </div>
      </Card>

      <Card title={t('auth.session.title')}>
        <p className="mb-3 break-words text-sm text-muted">
          {user
            ? t('auth.session.signedInAs', { email: user.email ?? '' })
            : t('auth.session.signedOut')}
        </p>
        {user && <SignOutButton />}
      </Card>

      <ResetAppDataCard />

      <p className="text-xs text-muted">{t('settings.storedLocally')}</p>
    </div>
  );
}
